import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { STOCK_STATUSES } from "@/data/mockData";
import { isOrderable, orderCtaLabel } from "@/lib/stock";

export function StockBadge({ status }) {
  const meta = STOCK_STATUSES[status] || STOCK_STATUSES.in_stock;
  const tones = {
    green: "bg-green-900/10 text-green-800 border-green-900/30",
    amber: "bg-amber-100/60 text-amber-800 border-amber-700/30",
    red: "bg-red-900/10 text-red-800 border-red-900/30",
    gold: "bg-[#6e5022]/10 text-[#6e5022] border-[#6e5022]/40",
    neutral: "bg-[#281a12]/5 text-[#4A3728] border-[#4A3728]/30",
  };
  return (
    <span
      data-testid={`stock-badge-${status}`}
      className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${tones[meta.tone]}`}
    >
      {meta.label}
    </span>
  );
}

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const orderable = isOrderable(product);

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col aa-fade-up"
      style={{ animationDelay: `${index * 0.06}s`, opacity: 0 }}
    >
      <Link
        to={`/product/${product.id}`}
        data-testid={`product-card-image-link-${product.id}`}
        className="block relative overflow-hidden bg-[#F4F1EA] aspect-[4/5]"
      >
        <img
          src={product.image}
          alt={product.title}
          loading="lazy"
          className="w-full h-full object-contain p-3 transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <StockBadge status={product.stockStatus} />
        </div>
      </Link>

      <div className="pt-5 flex flex-col flex-1">
        <div className="overline text-[#946f36] mb-1.5">
          {product.category === "books" ? "Ace Asia Books" : "Ace Asia Collectibles"}
        </div>
        <Link
          to={`/product/${product.id}`}
          data-testid={`product-card-title-${product.id}`}
          className="font-serif-display text-2xl leading-snug text-[#281a12] hover:text-[#6e5022] transition-colors"
        >
          {product.title}
        </Link>
        <p className="mt-2 text-sm text-[#4A3728] leading-relaxed line-clamp-2">
          {product.shortDescription}
        </p>

        <div className="mt-4 pt-4 border-t border-[#6e5022]/20 flex items-center justify-between gap-3">
          {product.priceTBA ? (
            <span
              className="text-xs text-[#946f36] leading-tight max-w-[58%]"
              data-testid={`product-card-price-${product.id}`}
            >
              Prices available at product launch in 2027
            </span>
          ) : (
            <span className="font-serif-display text-xl text-[#281a12]" data-testid={`product-card-price-${product.id}`}>
              SGD {product.price.toFixed(2)}
            </span>
          )}
          <button
            data-testid={`product-card-add-${product.id}`}
            disabled={!orderable}
            onClick={() => addItem(product, 1)}
            className={`text-xs uppercase tracking-[0.18em] px-4 py-2.5 transition-colors whitespace-nowrap ${
              !orderable
                ? "border border-[#4A3728]/30 text-[#4A3728]/60 cursor-not-allowed"
                : "border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7]"
            }`}
          >
            {product.priceTBA ? "Coming 2027" : orderCtaLabel(product)}
          </button>
        </div>
      </div>
    </article>
  );
}
